/** The operator's option lists the tour record reads outside its pickers: the booking
 *  systems and accounts an alias may name (the identifiers table, the readiness rules)
 *  and the launch policies with their flags (the effective-flags grid of a policy the
 *  draft picked). Cached per operator for the page's life — small lists. */
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getOperatorPreCheck, operatorPreCheckKey } from '@/api/operatorPrecheck';
import * as toursApi from '@/api/tours';
import type { PickerOption, RestechAccountOption } from '@/api/tours';
import type { CustomFieldDefinition } from '@/features/precheck/model';
import { usePermissions } from '@/hooks/usePermissions';

const FIVE_MINUTES = 5 * 60 * 1000;

export function useRestechAccounts(tenantId: number | null | undefined) {
  return useQuery<RestechAccountOption[]>({
    queryKey: ['tours', 'restech-accounts', tenantId],
    queryFn: async () => (await toursApi.restechAccounts(tenantId!)).results,
    enabled: Boolean(tenantId),
    staleTime: FIVE_MINUTES,
  });
}

export function useLaunchPolicies(tenantId: number | null | undefined) {
  return useQuery<PickerOption[]>({
    queryKey: ['tours', 'launch-policies', tenantId],
    queryFn: async () => (await toursApi.picker(tenantId!, 'launch_policies')).results,
    enabled: Boolean(tenantId),
    staleTime: FIVE_MINUTES,
  });
}

/** "Bokun · Default" for a stored pair, from the operator's list; the raw code when the
 *  list does not have it (yet). */
export function restechLabel(
  list: readonly RestechAccountOption[] | undefined,
  integration: string | null,
  account: string | null,
): { text: string; known: boolean | null } {
  if (!integration) return { text: 'Any system', known: true };
  if (!list) return { text: account ? `${integration} · ${account}` : integration, known: null };
  const hit = list.find((r) => r.code === integration && r.account === (account || null));
  if (hit) return { text: hit.name, known: true };
  return { text: account ? `${integration} · ${account}` : integration, known: false };
}

/** The operator's tag categories (names and codes of the pre-check-in questions). */
export function useTagCategoryOptions(tenantId: number | null | undefined) {
  return useQuery<PickerOption[]>({
    queryKey: ['tours', 'tag-categories', tenantId],
    // The whole list (the server's cap): a name looked up here must not be missed.
    queryFn: async () => (await toursApi.picker(tenantId!, 'tag_categories', undefined, undefined, undefined, 200)).results,
    enabled: Boolean(tenantId),
    staleTime: FIVE_MINUTES,
  });
}

/** The operator's enabled waivers (names of the pre-check-in waivers — the stored list
 *  carries ids only). */
export function useWaiverOptions(tenantId: number | null | undefined) {
  return useQuery<PickerOption[]>({
    queryKey: ['tours', 'waivers', tenantId],
    queryFn: async () => (await toursApi.picker(tenantId!, 'waivers', undefined, undefined, undefined, 200)).results,
    enabled: Boolean(tenantId),
    staleTime: FIVE_MINUTES,
  });
}

/** The operator's custom pre-check-in fields (its Pre-check-in tab's GET, `definitions`) —
 *  what "Add a field" offers by id. Read with `console_top_view` only (the GET's own
 *  permission); undefined without it, while loading or after a failure: the menu then
 *  offers "Custom field…" alone, as before. The GET's cache is the tab's (one key). */
export function useOperatorCustomFields(tenantId: number | null | undefined): CustomFieldDefinition[] | undefined {
  const { hasPerm } = usePermissions();
  const q = useQuery({
    queryKey: operatorPreCheckKey(tenantId ?? 0),
    queryFn: ({ signal }) => getOperatorPreCheck(tenantId!, signal),
    enabled: Boolean(tenantId) && hasPerm('console_top_view'),
    staleTime: FIVE_MINUTES,
    retry: false,
  });
  const definitions = q.data?.definitions;
  return useMemo(() => definitions?.map(({ id, name, type }) => ({ id, name, type })), [definitions]);
}
